import { bus } from '../core/event-bus.js';
import { playerManager } from '../core/player-manager.js';
import { getParsedCompanions } from '../core/vast-xml-parser.js';

const IAB_SLOTS = [
  { id: '300x250',  w: 300,  h: 250,  label: 'Medium Rectangle' },
  { id: '728x90',   w: 728,  h: 90,   label: 'Leaderboard' },
  { id: '160x600',  w: 160,  h: 600,  label: 'Wide Skyscraper' },
  { id: '300x600',  w: 300,  h: 600,  label: 'Half Page' },
  { id: '970x250',  w: 970,  h: 250,  label: 'Billboard' },
  { id: '970x90',   w: 970,  h: 90,   label: 'Super Leaderboard' },
  { id: '320x50',   w: 320,  h: 50,   label: 'Mobile Leaderboard' },
  { id: '300x50',   w: 300,  h: 50,   label: 'Mobile Banner' },
  { id: '336x280',  w: 336,  h: 280,  label: 'Large Rectangle' },
  { id: '250x250',  w: 250,  h: 250,  label: 'Square' },
  { id: '120x600',  w: 120,  h: 600,  label: 'Skyscraper' },
  { id: '468x60',   w: 468,  h: 60,   label: 'Full Banner' },
  { id: '480x480',  w: 480,  h: 480,  label: 'Square (Large)' },
];

export function initCompanionPanel() {
  const display = document.getElementById('companion-display');
  _renderSlots(display);

  bus.on('ad:event', ({ type }) => {
    if (type !== 'adstarted') return;
    _fillSlots(display);
  });

  // Re-fill when parsed data arrives late (after adstarted)
  bus.on('vast:companionsParsed', () => {
    if (playerManager.rmpVast?.adOnStage) _fillSlots(display);
  });

  bus.on('session:started', () => {
    _clearSlots(display);
  });
  bus.on('session:reset', () => {
    _clearSlots(display);
  });
}

function _renderSlots(container) {
  container.innerHTML = '';
  container.style.cssText = `
    display: flex; flex-wrap: wrap; gap: 12px; padding: 12px;
    align-items: flex-start; align-content: flex-start;
    overflow-y: auto; height: 100%;
  `;

  for (const slot of IAB_SLOTS) {
    const slotEl = document.createElement('div');
    slotEl.className = 'companion-slot';
    slotEl.setAttribute('data-testid', `companion-slot-${slot.id}`);
    slotEl.setAttribute('data-slot-id', slot.id);
    slotEl.setAttribute('data-slot-width', slot.w);
    slotEl.setAttribute('data-slot-height', slot.h);

    const scale = _getScale(slot.w, slot.h);

    slotEl.style.cssText = `
      width: ${slot.w * scale}px;
      height: ${slot.h * scale + 28}px;
      flex-shrink: 0;
    `;

    // Label
    const label = document.createElement('div');
    label.className = 'companion-slot-label';
    label.textContent = `${slot.label} (${slot.w}x${slot.h})`;
    slotEl.appendChild(label);

    // Ad container (scaled)
    const adArea = document.createElement('div');
    adArea.className = 'companion-slot-area';
    adArea.setAttribute('data-testid', `companion-area-${slot.id}`);
    adArea.style.cssText = `
      width: ${slot.w * scale}px;
      height: ${slot.h * scale}px;
      position: relative;
      overflow: hidden;
    `;

    // Inner container at real size, scaled down via transform
    const inner = document.createElement('div');
    inner.className = 'companion-slot-inner';
    inner.style.cssText = `
      width: ${slot.w}px;
      height: ${slot.h}px;
      transform: scale(${scale});
      transform-origin: top left;
      position: absolute; top: 0; left: 0;
    `;

    // Empty state placeholder
    const empty = document.createElement('div');
    empty.className = 'companion-slot-empty';
    empty.textContent = `${slot.w}×${slot.h}`;
    inner.appendChild(empty);

    adArea.appendChild(inner);
    slotEl.appendChild(adArea);
    container.appendChild(slotEl);
  }
}

function _fillSlots(container) {
  const rv = playerManager.rmpVast;
  if (!rv) return;

  _clearSlots(container);

  const parsed = getParsedCompanions();
  const endCardSizes = new Set(
    parsed
      .filter(c => c.renderingMode === 'end-card')
      .map(c => `${c.width}x${c.height}`)
  );

  try {
    const list = rv.getCompanionAdsList(2000, 2000);
    if (!list || list.length === 0) return;

    for (let i = 0; i < list.length; i++) {
      const companion = list[i];
      const sizeKey = `${companion.width}x${companion.height}`;

      if (endCardSizes.has(sizeKey)) continue;

      const slot = IAB_SLOTS.find(s => s.id === sizeKey);
      if (!slot) continue;

      const inner = container.querySelector(
        `[data-slot-id="${sizeKey}"] .companion-slot-inner`
      );
      if (!inner) continue;

      inner.innerHTML = '';

      const adEl = rv.getCompanionAd(i);
      if (adEl) {
        inner.appendChild(adEl);
        inner.closest('.companion-slot').classList.add('filled');
      }
    }
  } catch (e) {
    console.warn('[CompanionPanel] Error filling slots:', e);
  }
}

function _clearSlots(container) {
  const slots = container.querySelectorAll('.companion-slot');
  slots.forEach((slot) => {
    slot.classList.remove('filled');
    const inner = slot.querySelector('.companion-slot-inner');
    if (inner) {
      const w = slot.getAttribute('data-slot-width');
      const h = slot.getAttribute('data-slot-height');
      inner.innerHTML = `<div class="companion-slot-empty">${w}×${h}</div>`;
    }
  });
}

function _getScale(w, h) {
  // Scale down large slots to fit the panel; keep small ones at 1:1
  const maxW = 350;
  const maxH = 250;
  const scaleW = w > maxW ? maxW / w : 1;
  const scaleH = h > maxH ? maxH / h : 1;
  return Math.min(scaleW, scaleH);
}
